import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Flyout, FlyoutTitle } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { getIsTaxExcludedMenuPrice } from 'constants/logic';
import useModifierGroups from 'features/Sku/Modifiers/useModifierGroups';
import { useModifierLinkAccess } from 'features/Sku/Modifiers/useModifierItemLinks';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenue } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import { fetchSkuGroups, fetchSkuSubgroups } from '../SkuItems.helpers';
import { getCategoryId, getCategoryMenuId, getMenuId, isUncategorizedCategory } from '../SkuMenuData.normalizers';
import { ITEM_SAVE_STATUS } from '../SkuItems.constants';
import useSkuItemSubmission from '../useSkuItemSubmission';
import { ADD_CATEGORY_MODAL_NAME } from './AddCategoryModal';
import SkuAddItemFormContent from './SkuAddItemFormContent';
import { messages } from './SkuAddItemForm.messages';

import './SkuItemForm.scss';
import './SkuAddItemForm.scss';

const { block } = bem('item-form');
export const SKU_ADD_ITEM_FLYOUT_ID = 'sku-add-item-flyout';

const SkuAddItemForm = ({ isLoading, currencyCode, preselectedCategoryIds, categoriesRefreshKey, onSuccess }) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const venue = useSelector(selectActiveVenue);
  const { closeFlyout } = useContext(FlyoutContext);

  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [localRefreshKey, setLocalRefreshKey] = useState(0);

  const { venueId, countryCodeShort } = venue;
  const isTaxExcludedMenuPrice = getIsTaxExcludedMenuPrice(countryCodeShort);
  const menuPriceDescription = isTaxExcludedMenuPrice
    ? messages.taxExcludedMenuPriceDescription
    : messages.taxIncludedMenuPriceDescription;

  const { canAuthorModifierLinks: showModifiersSection } = useModifierLinkAccess();
  const modifierGroupsQuery = useModifierGroups(showModifiersSection ? venueId : undefined);
  const navigate = useNavigate();

  const { handleSubmit } = useSkuItemSubmission({ venue, currencyCode, onSuccess });
  const [saveFailure, setSaveFailure] = useState(null);

  const handleRetryModifierGroups = useCallback(() => modifierGroupsQuery.refetch(), [modifierGroupsQuery]);

  const submitForOutcome = useCallback(
    async values => {
      const outcome = await handleSubmit(values);
      setSaveFailure(outcome && outcome.status !== ITEM_SAVE_STATUS.SAVED ? outcome : null);
    },
    [handleSubmit]
  );

  const handleAddModifierGroup = useCallback(() => {
    closeFlyout();
    navigate(makePath.skuModifierAdd({ venueId }));
  }, [closeFlyout, navigate, venueId]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        if (localRefreshKey === 0) setCategoriesLoading(true);
        const [groupsData, subgroupsData] = await Promise.all([fetchSkuGroups(venueId), fetchSkuSubgroups(venueId)]);

        setGroups((groupsData || []).filter(group => !!getMenuId(group)));
        setSubgroups(
          (subgroupsData || []).filter(
            subgroup => !!getCategoryId(subgroup) && !!getCategoryMenuId(subgroup) && !isUncategorizedCategory(subgroup)
          )
        );
      } catch (error) {
        reportError('Error loading SKU categories', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, [venueId, categoriesRefreshKey, localRefreshKey]);

  const handleAddCategory = useCallback(() => {
    const defaultMenuId = preselectedCategoryIds?.length
      ? getCategoryMenuId(subgroups.find(s => getCategoryId(s) === preselectedCategoryIds[0]))
      : undefined;

    dispatch(
      openModal(ADD_CATEGORY_MODAL_NAME, {
        menus: groups,
        venueId,
        defaultMenuId,
        onSuccess: () => setLocalRefreshKey(prev => prev + 1),
      })
    );
  }, [dispatch, groups, venueId, preselectedCategoryIds, subgroups]);

  const preselectedCategories = useMemo(() => {
    if (!preselectedCategoryIds?.length) {
      return [];
    }

    const idSet = new Set(preselectedCategoryIds);
    return subgroups.filter(subgroup => idSet.has(getCategoryId(subgroup)));
  }, [preselectedCategoryIds, subgroups]);

  // Intentionally includes categoriesLoading so the form remounts when categories
  // finish loading, applying preselection via defaultValues rather than setValue.
  const formKey = useMemo(
    () => `${(preselectedCategoryIds || []).join(',')}::${categoriesLoading}`,
    [preselectedCategoryIds, categoriesLoading]
  );

  return (
    <Flyout
      id={SKU_ADD_ITEM_FLYOUT_ID}
      className={`${block()} sku-add-item-flyout`}
      onClose={closeFlyout}
      overrideOutsideClick={() => true}
      showBackButton={isMobile}>
      <FlyoutTitle>
        <Message message={messages.addItem} />
      </FlyoutTitle>
      <SkuAddItemFormContent
        isLoading={isLoading}
        categoriesLoading={categoriesLoading}
        groups={groups}
        subgroups={subgroups}
        currencyCode={currencyCode}
        preselectedCategories={preselectedCategories}
        menuPriceDescription={menuPriceDescription}
        formKey={formKey}
        onSubmit={submitForOutcome}
        onAddCategory={handleAddCategory}
        modifierGroups={modifierGroupsQuery.data || []}
        modifierGroupsLoading={modifierGroupsQuery.isLoading}
        modifierGroupsLoadFailed={modifierGroupsQuery.isError}
        onRetryModifierGroups={handleRetryModifierGroups}
        onAddModifierGroup={handleAddModifierGroup}
        showModifiersSection={showModifiersSection}
        saveFailure={saveFailure}
        onDismissSaveFailure={() => setSaveFailure(null)}
      />
    </Flyout>
  );
};

SkuAddItemForm.propTypes = {
  isLoading: PropTypes.bool,
  currencyCode: PropTypes.string,
  preselectedCategoryIds: PropTypes.arrayOf(PropTypes.string),
  categoriesRefreshKey: PropTypes.number,
  onSuccess: PropTypes.func,
};

SkuAddItemForm.defaultProps = {
  isLoading: false,
  currencyCode: undefined,
  preselectedCategoryIds: [],
  categoriesRefreshKey: 0,
  onSuccess: null,
};

export default SkuAddItemForm;
